<template>
  <div class="card">
    <div style="background: #DBE7F2" class="p-2">
      <TitleButton
          :showBtn="false"
          :showSettingBtn="false"
          :title="'Restaurant POS Table'"
          @onClickCloseButton="closeButton"
      />
    </div>

    <div class="px-2 pt-2">
      <RestaurantPosSummary ref="summaryRef" />

      <div class="d-flex justify-content-end mt-1">
        <div class="view-tabs">
          <div
              class="view-tab"
              :class="{ active: activeView === 'grid' }"
              @click="setActiveView('grid')"
          >
            <i class="fas fa-th-large"></i>
          </div>
          <div
              class="view-tab"
              :class="{ active: activeView === 'list' }"
              @click="setActiveView('list')"
          >
            <i class="fas fa-list"></i>
          </div>
        </div>
      </div>
    </div>

    <div v-show="activeView === 'grid'">
      <RestaurantPosTableGrid
          ref="gridRef"
          @select="openOrderModal"
      />
    </div>

    <div v-show="activeView === 'list'">
      <RestaurantPosOrderList
          ref="orderListRef"
          @addOrder="openAddNew"
          @editOrder="openOrderForEdit"
          @createInvoice="openInvoiceModal"
      />
    </div>
  </div>

  <RestaurantPosTableOrderModal
      v-if="modalOpen"
      :key="modalKey"
      :tableName="activeTable.name"
      :selectable="selectable"
      @close="closeOrderModal"
  />

  <RestaurantPosInvoiceModal
      v-if="invoiceModalOpen"
      :key="invoiceOrderId"
      :tableName="activeTable.name"
      @close="closeInvoiceModal"
  />
</template>

<script setup>
import {computed, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import TitleButton from '@/components/atom/TitleButton.vue';
import RestaurantPosTableOrderModal from '@/components/molecule/company/pos-invoice/RestaurantPosTableOrderModal.vue';
import RestaurantPosInvoiceModal from '@/components/molecule/company/pos-invoice/RestaurantPosInvoiceModal.vue';
import RestaurantPosTableGrid from '@/components/molecule/company/pos-invoice/RestaurantPosTableGrid.vue';
import RestaurantPosOrderList from '@/components/molecule/company/pos-invoice/RestaurantPosOrderList.vue';
import RestaurantPosSummary from '@/components/molecule/company/pos-invoice/RestaurantPosSummary.vue';

const router = useRouter();
const route = useRoute();

const modalOpen = ref(false);
const invoiceModalOpen = ref(false);
const invoiceOrderId = ref(null);
const activeTable = ref({});
const activeView = ref(route.query.active_view || 'grid');
const selectable = ref(false);
const gridRef = ref(null);
const orderListRef = ref(null);
const summaryRef = ref(null);

const refreshAll = async () => {
  if (gridRef.value) {
    await gridRef.value.getPosTables();
  }
  if (orderListRef.value) {
    await orderListRef.value.getOrders();
  }
  if (summaryRef.value) {
    await summaryRef.value.getSummary();
  }
};

const modalKey = computed(() => {
  if (selectable.value) {
    return 'add-new';
  }
  return activeTable.value.id;
});

const setActiveView = (view) => {
  activeView.value = view;
  router.replace({query: {...route.query, active_view: view}});
};

const closeButton = () => {
  router.back();
};

const openOrderModal = async (table) => {
  selectable.value = false;
  activeTable.value = table;
  const newQuery = {...route.query, service_resource_id: table.id};
  if (table.order_sales_id) {
    newQuery.order_sales_id = table.order_sales_id;
  }
  if (!table.order_sales_id) {
    delete newQuery.order_sales_id;
  }
  await router.replace({query: newQuery});
  modalOpen.value = true;
};

const openOrderForEdit = async (order) => {
  selectable.value = false;
  activeTable.value = {id: order.orderable_id, name: order.table_name};
  const newQuery = {...route.query, service_resource_id: order.orderable_id, order_sales_id: order.id};
  await router.replace({query: newQuery});
  modalOpen.value = true;
};

const openAddNew = async () => {
  selectable.value = true;
  activeTable.value = {};
  const newQuery = {...route.query};
  delete newQuery.order_sales_id;
  delete newQuery.service_resource_id;
  await router.replace({query: newQuery});
  modalOpen.value = true;
};

const closeOrderModal = async () => {
  modalOpen.value = false;
  selectable.value = false;
  const newQuery = {...route.query};
  delete newQuery.order_sales_id;
  delete newQuery.service_resource_id;
  router.replace({query: newQuery});
  await refreshAll();
};

const openInvoiceModal = async (order) => {
  activeTable.value = {id: order.orderable_id, name: order.table_name};
  invoiceOrderId.value = order.id;
  const newQuery = {...route.query, sales_order_id: order.id, from_restaurant: 1};
  await router.replace({query: newQuery});
  invoiceModalOpen.value = true;
};

const closeInvoiceModal = async () => {
  invoiceModalOpen.value = false;
  const newQuery = {...route.query};
  delete newQuery.sales_order_id;
  delete newQuery.from_restaurant;
  router.replace({query: newQuery});
  await refreshAll();
};
</script>

<style scoped>
.view-tabs {
  display: inline-flex;
  border-radius: 12px;
  padding: 5px;
  gap: 6px;
}

.view-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 12px;
  border-radius: 9px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: #6b7a90;
  background: #e9ecef;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.view-tab i {
  font-size: 15px;
}

.view-tab:not(.active):hover {
  color: #7367f0;
  background: #e3e0fb;
}

.view-tab.active {
  background: #7367f0;
  color: #fff;
  box-shadow: 0 3px 8px rgba(115, 103, 240, 0.4);
}
</style>
